/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, PrimaryButton } from '@fluentui/react/lib/Button';
import { Callout, DirectionalHint, ICalloutProps } from '@fluentui/react/lib/Callout';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { GetStartedList } from 'src/luis/verticals/getStartedGuide/components/GetStartedList';

const messages = defineMessages({
    getStartedButtonText: {
        id: 'portal.getStartedGuide.getStartedButtonText',
        defaultMessage: 'Get Started'
    }
});

const { styleDiv, styleComponent } = getStylistV2('GetStartedGuide');

const Button = styleComponent<IButtonProps>(PrimaryButton)('Button', theme => ({
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 4,
    height: 32,
    boxShadow: theme.buttonShadow
}));

const Container = styleDiv('Container', {
    position: 'fixed',
    bottom: 40,
    right: 50
});

const StyledCallout = styleComponent<ICalloutProps>(Callout)('StyledCallout', {
    width: 400,
    marginBottom: 10
});

const labelId = getId('CalloutLabel');
const descriptionId = getId('CalloutDescription');

export const GetStartedGuide = () => {
    const [isCalloutVisible, setCalloutVisible] = React.useState(false);
    const buttonWrapperRef = React.useRef<HTMLDivElement>();
    const { localize } = useLocalization();

    return (
        <>
            <Container originalRef={buttonWrapperRef}>
                <Button
                    data-automation-id="getStartedButton"
                    text={localize(messages.getStartedButtonText)}
                    onClick={() => setCalloutVisible(!isCalloutVisible)}
                    iconProps={{ iconName: 'Lightbulb' }}
                />
            </Container>
            {isCalloutVisible && (
                <StyledCallout
                    ariaLabelledBy={labelId}
                    ariaDescribedBy={descriptionId}
                    gapSpace={0}
                    target={buttonWrapperRef.current}
                    isBeakVisible={false}
                    beakWidth={16}
                    onDismiss={() => setCalloutVisible(false)}
                    directionalHint={DirectionalHint.topRightEdge}
                    setInitialFocus>
                    <GetStartedList />
                </StyledCallout>
            )}
        </>
    );
};
